import { Link } from "react-router-dom";
import useBreakpoint from "../../hooks/useBreakpoint";
import logo from "../../images/OneEssilorLuxotticaLogo.svg";
import { Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ShareWidget from "../share-widget";
import { HomePageLanguageSelect } from "../../routes/settings";
import LanguageContext from "../../context/language";
import UserContext from "../../context/user";
import classNames from "classnames";
import useBlockScroll from "../../hooks/useBlockScroll";
import Icon from "../icon";
import {
  HeaderContext,
  HeaderItem,
  Megamenu,
  MegamenuMobile,
  MyProfile,
  UserImage,
} from "./subcomponents";

const EMPTY_ARRAY = [];

const MENU_VISUALIZATION_MODE = {
  hidden: "hidden",
  mobile: "mobile",
  desktop: "desktop",
};

const HeaderNew = (props) => {
  const { language, languages, cmsLanguages, cmsLang } = useContext(LanguageContext);
  const { loadProfile, callApi } = useContext(UserContext);
  const menu = props.menu || EMPTY_ARRAY;

  const [selectedLanguage, setSelectedLanguage] = useState();
  const currentLanguage = useMemo(() => {
    if (languages && language) {
      return languages.find((item) => item.value === language);
    }
  }, [language, languages]);
  useEffect(() => {
    if (cmsLang) {
      setSelectedLanguage(cmsLanguages?.find((lang) => lang.value === cmsLang));
    }
  }, [cmsLang, cmsLanguages]);

  const updateLanguage = async (e) => {
    setSelectedLanguage(e);

    if (e?.profile && e?.profile !== currentLanguage?.value) {
      try {
        await callApi(`/profile?language=${e?.profile}`, {
          method: "POST",
        });
        await onUpdate();
      } catch (err) {
        console.error("Error updating language: ", err);
        setSelectedLanguage(currentLanguage?.value);
      }
    }
  };

  const onUpdate = useCallback(async () => {
    await loadProfile();
  }, [loadProfile]);

  const isBelow991 = useBreakpoint(991);
  const isBelow1230 = useBreakpoint(1230);

  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [openTab, setOpenTab] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [openUserSubmenu, setOpenUserSubmenu] = useState(false);
  const headerTopRef = useRef(null);
  const headerMenuRef = useRef(null);
  const [menuVisualization, setMenuVisualization] = useState(MENU_VISUALIZATION_MODE.hidden);

  useBlockScroll(!!openTab || !!menuOpen);

  useEffect(() => {
    if (!isBelow1230 && menuOpen && menuVisualization !== MENU_VISUALIZATION_MODE.mobile) {
      setMenuOpen(false);
      return;
    }

    if (isBelow1230 && openTab) {
      setOpenTab("");
      setMenuOpen(true);
    }
  }, [isBelow1230, openTab, menuOpen, menuVisualization]);

  useEffect(() => {
    function calc() {
      if (!menu) {
        return;
      }

      if (isBelow1230) {
        setMenuVisualization(MENU_VISUALIZATION_MODE.mobile);
        return;
      }

      // tolleranza per evitare di schiacciare la lente del cerca contro il resto dell'header
      if (headerMenuRef.current.scrollWidth > headerTopRef.current.clientWidth - 50) {
        setMenuVisualization(MENU_VISUALIZATION_MODE.mobile);
      } else {
        setMenuVisualization(MENU_VISUALIZATION_MODE.desktop);
      }
    }

    calc();

    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [isBelow1230, menu]);

  return (
    <HeaderContext.Provider
      value={{
        openTab,
        menuOpen,
        menu,
        setOpenTab,
        setMenuOpen,
        selectedLanguage,
        cmsLanguages,
        updateLanguage,
        openUserSubmenu,
        setOpenUserSubmenu,
      }}
    >
      <header className="header-new">
        {!isBelow991 && (
          <div className="header-new__top">
            <Suspense>
              <ShareWidget />
            </Suspense>
            <div
              className={classNames(
                "header-new__language-wrapper",
                !selectedLanguage && "loading-skeleton"
              )}
            >
              <HomePageLanguageSelect
                options={cmsLanguages}
                placeholder={currentLanguage?.label}
                label="Language"
                selectedLanguage={selectedLanguage}
                id="language"
                updateLanguage={updateLanguage}
                menuIsOpen={languageMenuOpen}
                setMenuIsOpen={setLanguageMenuOpen}
              />
            </div>
          </div>
        )}
        {(openTab || menuOpen || openUserSubmenu) && (
          <div
            className="header-new__background"
            onMouseEnter={() => {
              if (languageMenuOpen) {
                return;
              }

              if (
                window.matchMedia(`@media (pointer: coarse)`).matches ||
                menuVisualization === MENU_VISUALIZATION_MODE.mobile
              ) {
                return;
              }
              setOpenTab("");
              setMenuOpen(false);
              setOpenUserSubmenu(false);
            }}
            onClick={() => {
              if (languageMenuOpen) {
                return;
              }
              setOpenTab("");
              setMenuOpen(false);
              setOpenUserSubmenu(false);
            }}
          ></div>
        )}
        <div
          className={classNames("header-new__content", {
            "header-new__content--home-page": window.location.pathname === "/",
          })}
        >
          <div
            className={classNames("header-new__megamenu", {
              "header-new__megamenu--open": !!openTab || !!openUserSubmenu,
            })}
          >
            <Link
              to="/"
              className="header-new__logo-cta"
              onClick={() => {
                setMenuOpen(false);
                setOpenTab("");
                setOpenUserSubmenu(false);
              }}
            >
              <img
                className={classNames("header-new__logo", {
                  "header-new__logo--open": openTab || menuOpen || openUserSubmenu,
                })}
                src={logo}
                alt="OneEssilorLuxottica"
              />
            </Link>
            {!isBelow1230 && (
              <div className="header-new__center" ref={headerTopRef}>
                <div className="header-new__sentinel" aria-hidden ref={headerMenuRef}>
                  {menu.map((i) => {
                    return (
                      <div className="header-new__sentinel-item" key={i.label}>
                        {i.label}
                      </div>
                    );
                  })}
                </div>

                {menuVisualization === "desktop" && (
                  <ul className="header-new__top-categories">
                    {menu.map((i) => {
                      return (
                        <li key={i.label}>
                          <HeaderItem
                            item={i}
                            setOpenTab={setOpenTab}
                            setMenuOpen={setMenuOpen}
                            openTab={openTab}
                            openUserSubmenu={openUserSubmenu}
                            setOpenUserSubmenu={setOpenUserSubmenu}
                            languageMenuOpen={languageMenuOpen}
                          />
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
            <div className="header-new__buttons">
              <button
                className={classNames("header-new__button header-new__search", {
                  "header-new__button--open": openTab || openUserSubmenu,
                })}
                onClick={() => props.setSearchVisible(true)}
                aria-label="Search"
              >
                <Icon name="search" />
              </button>
              {!isBelow1230 && menuVisualization === MENU_VISUALIZATION_MODE.desktop && (
                <div
                  className={classNames("header-new__user-image-wrapper", {
                    "header-new__user-image-wrapper--open": openTab || openUserSubmenu,
                    "header-new__user-image-wrapper--user-open": openUserSubmenu,
                  })}
                >
                  <UserImage
                    onEnter={() => {
                      setMenuOpen(false);
                      setOpenTab("");
                      setOpenUserSubmenu(true);
                    }}
                  />
                  {openUserSubmenu && (
                    <div className="header-new__user-submenu">
                      <MyProfile desktop />
                    </div>
                  )}
                </div>
              )}
              {(isBelow1230 ||
                (!isBelow1230 && menuVisualization === MENU_VISUALIZATION_MODE.mobile)) && (
                <button
                  className={classNames("header-new__button header-new__hamburger", {
                    "header-new__button--open": openTab || openUserSubmenu,
                  })}
                  onClick={() => {
                    if (menuOpen) {
                      setMenuOpen(false);
                    } else setMenuOpen(true);
                  }}
                >
                  {menuOpen ? <Icon name="x-icon" /> : <Icon name="hamburger" />}
                </button>
              )}
            </div>

            {openTab && (
              <Megamenu
                menu={menu}
                openTabUrl={openTab}
                setMenuOpen={setMenuOpen}
                setOpenTab={setOpenTab}
              />
            )}
            {menuOpen && <MegamenuMobile />}
          </div>
        </div>
      </header>
    </HeaderContext.Provider>
  );
};

export default HeaderNew;
